import {useCallback, useRef, useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import GenerateTicketReplyDraftMutation from '../../../../../mutations/ticketing/tickets/generateTicketReplyDraftMutation';

const NOT_CONFIGURED = 'AI drafting is not set up for this workspace yet. Ask an admin to enable it.';
const GENERIC = 'Could not draft a reply just now. Your message was left untouched — please try again.';

/**
 * Turns a raw mutation error into copy an agent can act on.
 *
 * The backend sends a real reason (a rate-limit refusal names the cap and the
 * reset instant); graphql appends a correlation id. Rate-limit and not-configured
 * get their own wording, and anything else falls back to a generic failure that
 * makes clear the editor was not modified.
 */
export function friendlyDraftError(raw: string): string {
	if (/not configured|not set up/i.test(raw)) return NOT_CONFIGURED;

	const capMatch = /\((\d+) per day\)/.exec(raw);
	if (/limit reached|rate limit/i.test(raw) || capMatch) {
		const cap = capMatch ? capMatch[1] : null;
		const resetMatch = /\d{4}-\d{2}-\d{2}T[\d:.]+Z/.exec(raw);
		const resetAt = resetMatch ? new Date(resetMatch[0]) : null;
		const when =
			resetAt && !isNaN(resetAt.getTime())
				? resetAt.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'}) +
				  ' on ' +
				  resetAt.toLocaleDateString([], {month: 'short', day: 'numeric'})
				: null;
		const capPart = cap ? `You have used all ${cap} AI drafts for this ticket today.` : 'Daily AI draft limit reached.';
		return when ? `${capPart} You can draft again at ${when}.` : capPart;
	}

	return GENERIC;
}

interface Options {
	onCompleted?: (draftText: string) => void;
	onError?: (error: Error) => void;
}

/**
 * Requests an AI-drafted reply for a ticket.
 *
 * `generate` resolves with the draft text and rejects on failure, so the caller can
 * `await` it and only touch the editor on success. `error` carries display-ready
 * copy for the failure cases (over cap, AI not configured, generation failed).
 */
export function useGenerateReplyDraft(options?: Options) {
	const [inFlight, setInFlight] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Keep the latest callbacks out of `generate`'s identity so wiring it to an
	// onClick does not re-create the handler on every parent render.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const reset = useCallback(() => setError(null), []);

	const generate = useCallback((ticketId: string): Promise<string> => {
		setInFlight(true);
		setError(null);
		return new Promise<string>((resolve, reject) => {
			commitTicketingMutation(
				GenerateTicketReplyDraftMutation,
				{ticketId},
				(response: unknown) => {
					const draftText = (response as {generateTicketReplyDraft?: {draftText?: string}} | null)
						?.generateTicketReplyDraft?.draftText;
					setInFlight(false);
					if (!draftText) {
						const err = new Error(GENERIC);
						setError(GENERIC);
						optionsRef.current?.onError?.(err);
						reject(err);
						return;
					}
					optionsRef.current?.onCompleted?.(draftText);
					resolve(draftText);
				},
				(rawError: unknown) => {
					const err = rawError instanceof Error ? rawError : new Error(String(rawError));
					setInFlight(false);
					setError(friendlyDraftError(err.message));
					optionsRef.current?.onError?.(err);
					reject(err);
				}
			);
		});
	}, []);

	return {generate, inFlight, error, reset};
}
